// src/Map.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import $ from "jquery";
import "imagemapster";
import "./map.css";

import image from "./../assets/map.png";
import compass from "./../assets/compasss.png";

import LocationLabels from "./LocationLabels";
import MapAreas, { plotCoordinates } from "./MapAreas";

import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "./firebase";

import PlotPanel from "./PlotPanel";
import ControlBar from "../ControlBar";

// Helpers
const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const ZOOM_KEY = "mapZoom";
const POSITION_KEY = "mapPosition";

const coordsToSvgPoints = (coordsString = "") => {
  const nums = String(coordsString)
    .split(",")
    .map((n) => Number(n.trim()))
    .filter((n) => !Number.isNaN(n));

  const pairs = [];
  for (let i = 0; i < nums.length; i += 2) {
    if (nums[i] != null && nums[i + 1] != null) {
      pairs.push(`${nums[i]},${nums[i + 1]}`);
    }
  }
  return pairs.join(" ");
};

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const normalizeFacing = (facing) => {
  const value = String(facing || "").trim().toUpperCase();

  if (!value || value === "N/A") return "N/A";

  const cleaned = value
    .replace(/\s+/g, "")
    .replace(/AND/g, "&")
    .replace(/\+/g, "&");

  if (cleaned === "EAST") return "East";
  if (cleaned === "WEST") return "West";
  if (cleaned === "NORTH") return "North";
  if (cleaned === "SOUTH") return "South";

  if (cleaned === "EAST&SOUTH" || cleaned === "SOUTH&EAST") return "East+South";
  if (cleaned === "EAST&NORTH" || cleaned === "NORTH&EAST") return "East+North";
  if (cleaned === "WEST&SOUTH" || cleaned === "SOUTH&WEST") return "West+South";
  if (cleaned === "WEST&NORTH" || cleaned === "NORTH&WEST") return "West+North";

  if (cleaned === "EAST&WEST" || cleaned === "WEST&EAST") return "East&West";
  if (cleaned === "NORTH&SOUTH" || cleaned === "SOUTH&NORTH") return "North&South";

  if (cleaned === "N&W" || cleaned === "W&N") return "West+North";
  if (cleaned === "N&E" || cleaned === "E&N") return "East+North";
  if (cleaned === "S&E" || cleaned === "E&S") return "East+South";
  if (cleaned === "S&W" || cleaned === "W&S") return "West+South";

  if (cleaned === "ALL") return "All";

  return "N/A";
};

const STATUS_COLORS = {
  available: "rgba(34, 197, 94, 0.82)",
  booked: "rgba(245, 158, 11, 0.82)",
  sold: "rgba(239, 68, 68, 0.71)",
  blocked: "rgba(168, 85, 247, 0.82)",
  unknown: "rgba(107, 114, 128, 0.82)",
};

const FACING_COLORS = {
  East: "rgba(169, 85, 247, 0.77)",
  West: "rgba(20, 184, 165, 0.69)",
  North: "rgba(59, 130, 246, 0.62)",
  South: "rgba(249, 115, 22, 0.62)",
  "East+South": "rgba(236, 72, 153, 0.62)",
  "East+North": "rgba(99, 102, 241, 0.62)",
  "West+South": "rgba(234, 179, 8, 0.62)",
  "West+North": "rgba(14, 164, 233, 0.72)",
  "East&West": "rgba(129, 141, 248, 0.7)",
  "North&South": "rgba(244, 114, 182, 0.62)",
  All: "rgba(239, 68, 68, 0.30)",
  "N/A": "rgba(255, 255, 255, 0.45)",
};

const Map = () => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const uiLocked = false;

  const [showStatusLabels, setShowStatusLabels] = useState(true);

  const [availableUnits, setAvailableUnits] = useState("Loading...");
  const [newsItems, setNewsItems] = useState([]);
  const [error, setError] = useState(null);

  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [selectedPlotData, setSelectedPlotData] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);

  const [hoveredPlotId, setHoveredPlotId] = useState(null);

  const [plotMetaMap, setPlotMetaMap] = useState({});
  const [colorMode, setColorMode] = useState("none");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [facingFilter, setFacingFilter] = useState("ALL");

  const isClickOnUI = useCallback((target) => {
    if (!target) return false;
    return Boolean(
      target.closest?.(".control-bar") ||
        target.closest?.(".sidebar") ||
        target.closest?.(".zoom-controls") ||
        target.closest?.(".plot-panel") ||
        target.closest?.("[data-ui='true']")
    );
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = image;
    img.onload = () => setIsImageLoaded(true);
    img.onerror = () => {
      console.error("Image failed to load");
      setIsImageLoaded(true);
      setError("Failed to load map image");
    };
  }, []);

  const fetchNewsData = useCallback(async () => {
    try {
      const newsCollection = collection(db, "news");
      const newsSnapshot = await getDocs(newsCollection);
      const newsData = newsSnapshot.docs.map((d) => ({
        newsId: d.id,
        ...d.data(),
      }));

      const projectNews = newsData
        .filter((n) => n.plotnews === "Clarks")
        .map((n) => (n.content || "").trim())
        .filter(Boolean);

      setNewsItems(projectNews.length > 0 ? projectNews : ["No news available"]);
    } catch (err) {
      console.error("Error fetching news:", err);
      setNewsItems(["Error fetching news"]);
      setError("Failed to load news data");
    }
  }, []);

  const fetchPlotsData = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "ClarksExoticaVilla"));
      const villas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const availableCount = villas.filter(
        (v) => normalizeStatus(v.Status) === "available"
      ).length;

      setAvailableUnits(availableCount);

      const metaMap = {};
      villas.forEach((villa) => {
        const key = villa.villaId || villa.id;
        if (!key) return;

        const normalizedStatus = normalizeStatus(villa.Status);
        const safeStatus = ["available", "booked", "sold", "blocked"].includes(
          normalizedStatus
        )
          ? villa.Status
          : "Unknown";

        metaMap[key] = {
          id: key,
          villaNumber: villa.villaNumber || key,
          status: safeStatus,
          facing: normalizeFacing(villa.facing),
          configuration: villa.configuration || "",
        };
      });

      setPlotMetaMap(metaMap);
    } catch (err) {
      console.error("Error fetching villas:", err);
      setAvailableUnits("Error");
      setError("Failed to load villa data");
    }
  }, []);

  const fetchPlotDetails = useCallback(async (plotId) => {
    setPanelLoading(true);
    try {
      const q = query(
        collection(db, "ClarksExoticaVilla"),
        where("villaId", "==", plotId),
        limit(1)
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setSelectedPlotData(null);
        return;
      }

      const docSnap = snap.docs[0];
      const data = docSnap.data();

      setSelectedPlotData({
        firestoreId: docSnap.id,
        villaId: data.villaId || plotId,
        villaNumber: data.villaNumber || "N/A",
        facing: data.facing || "N/A",
        configuration: data.configuration || "N/A",
        builtUpAreaSqft: data.builtUpAreaSqft ?? "N/A",
        landAreaSqft: data.LandAreaSqft ?? "N/A",
        premiumCharges: data.premiumCharges ?? 0,
        inclusivePrice: data.inclusivePrice ?? "N/A",
        floorPlan: data.floorPlan || "N/A",
        status: data.Status || "N/A",
        plotImage: data.plotImage || null,
      });
    } catch (err) {
      console.error("Error fetching villa details:", err);
      setSelectedPlotData(null);
    } finally {
      setPanelLoading(false);
    }
  }, []);

  const openPlotPanel = useCallback(
    (plotId) => {
      setSelectedPlotId(plotId);
      fetchPlotDetails(plotId);
    },
    [fetchPlotDetails]
  );

  const closePlotPanel = () => {
    setSelectedPlotId(null);
    setSelectedPlotData(null);

    const imgElement = mapRef.current;
    if (imgElement && $(imgElement).mapster) {
      try {
        $(imgElement).mapster("set", false);
      } catch (e) {
        console.log("Mapster clear selection skipped");
      }
    }
  };

  const getInitialZoom = () => {
    const savedZoom = sessionStorage.getItem(ZOOM_KEY);
    if (savedZoom) return parseFloat(savedZoom);
    return windowWidth <= 768 ? 3 : 1.8;
  };

  const getInitialPosition = () => {
    const savedPosition = sessionStorage.getItem(POSITION_KEY);
    if (savedPosition) return JSON.parse(savedPosition);
    return { x: 0, y: 0 };
  };

  const [zoom, setZoom] = useState(getInitialZoom());
  const [position, setPosition] = useState(getInitialPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragDistance, setDragDistance] = useState(0);
  const [wasDragged, setWasDragged] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [touchDistance, setTouchDistance] = useState(null);
  const [lastTouch, setLastTouch] = useState(null);

  const coords = useMemo(
    () => ({
      c1: { x: 1152, y: 1014 },
      c2: { x: 1439, y: 964 },
      c3: { x: 1088, y: 1593 },
      c4: { x: 2309, y: 1976 },
      c5: { x: 2342, y: 2060 },
      c6: { x: 2302, y: 2176 },
      c7: { x: 2044, y: 2405 },
    }),
    []
  );

  const selectedPlot = useMemo(() => {
    return plotCoordinates.find((plot) => plot.id === selectedPlotId) || null;
  }, [selectedPlotId]);

  const hoveredPlot = useMemo(() => {
    return plotCoordinates.find((plot) => plot.id === hoveredPlotId) || null;
  }, [hoveredPlotId]);

  const minZoom = windowWidth <= 768 ? 3 : 1.8;
  const maxZoom = windowWidth <= 768 ? 7 : 3.9;
  const zoomStep = 0.1;

  const statusOptions = useMemo(() => {
    return ["ALL", "Available", "Booked", "Sold", "Blocked"];
  }, []);

  const facingOptions = useMemo(() => {
    const values = Object.values(plotMetaMap)
      .map((p) => normalizeFacing(p.facing))
      .filter(Boolean);

    return ["ALL", ...Array.from(new Set(values))];
  }, [plotMetaMap]);

  const getLabelStyle = () => {
    if (windowWidth <= 768) {
      return {
        position: "absolute",
        fontSize: "2px",
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: "900",
        padding: "2px 5px",
        textAlign: "center",
        backgroundColor: "#000000c5",
        color: "white",
        zIndex: 1000,
        borderRadius: "10px",
      };
    } else if (windowWidth <= 1024) {
      return {
        position: "absolute",
        fontSize: "2px",
        fontFamily: "'Montserrat', sans-serif",
        padding: "3px 7px",
        fontWeight: "900",
        textAlign: "center",
        backgroundColor: "#000000c5",
        color: "white",
        zIndex: 1000,
        borderRadius: "10px",
      };
    } else {
      return {
        position: "absolute",
        fontSize: "4px",
        fontFamily: "'Montserrat', sans-serif",
        padding: "2px 6px",
        fontWeight: "900",
        textAlign: "center",
        backgroundColor: "#000000c5",
        color: "white",
        zIndex: 1000,
        borderRadius: "12px",
      };
    }
  };

  const calculateBoundaries = useCallback((targetZoom) => {
    const container = containerRef.current;
    const imgElement = mapRef.current;
    if (!container || !imgElement) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const imgWidth = imgElement.clientWidth * targetZoom;
    const imgHeight = imgElement.clientHeight * targetZoom;

    const maxX = (imgWidth - containerWidth) / 2;
    const maxY = (imgHeight - containerHeight) / 2;

    return {
      minX: imgWidth <= containerWidth ? 0 : -maxX,
      maxX: imgWidth <= containerWidth ? 0 : maxX,
      minY: imgHeight <= containerHeight ? 0 : -maxY,
      maxY: imgHeight <= containerHeight ? 0 : maxY,
    };
  }, []);

  const adjustPositionToBounds = useCallback(
    (currentZoom, currentPosition) => {
      const { minX, maxX, minY, maxY } = calculateBoundaries(currentZoom);
      const newPosition = {
        x: parseFloat(Math.min(maxX, Math.max(minX, currentPosition.x)).toFixed(2)),
        y: parseFloat(Math.min(maxY, Math.max(minY, currentPosition.y)).toFixed(2)),
      };
      sessionStorage.setItem(POSITION_KEY, JSON.stringify(newPosition));
      return newPosition;
    },
    [calculateBoundaries]
  );

  const getStatusColor = useCallback((status) => {
    const key = normalizeStatus(status);
    return STATUS_COLORS[key] || STATUS_COLORS.unknown;
  }, []);

  const getFacingColor = useCallback((facing) => {
    const key = normalizeFacing(facing);
    return FACING_COLORS[key] || FACING_COLORS["N/A"];
  }, []);

  const getPlotFilterFill = useCallback(
    (plotId) => {
      const meta = plotMetaMap[plotId];
      if (!meta) return null;

      if (colorMode === "status") {
        const plotStatus = normalizeStatus(meta.status);
        const selectedStatus = normalizeStatus(statusFilter);

        if (statusFilter !== "ALL" && plotStatus !== selectedStatus) {
          return "rgba(255,255,255,0.04)";
        }

        return getStatusColor(meta.status);
      }

      if (colorMode === "facing") {
        const plotFacing = normalizeFacing(meta.facing);
        const selectedFacing =
          facingFilter === "ALL" ? "ALL" : normalizeFacing(facingFilter);

        if (selectedFacing !== "ALL" && plotFacing !== selectedFacing) {
          return "rgba(255,255,255,0.04)";
        }

        return getFacingColor(meta.facing);
      }

      return null;
    },
    [plotMetaMap, colorMode, statusFilter, facingFilter, getStatusColor, getFacingColor]
  );

  const getLegendItems = () => {
    if (colorMode === "status") {
      return [
        { label: "Available", color: STATUS_COLORS.available },
        { label: "Booked", color: STATUS_COLORS.booked },
        { label: "Sold", color: STATUS_COLORS.sold },
        { label: "Blocked", color: STATUS_COLORS.blocked },
      ];
    }

    if (colorMode === "facing") {
      return [
        { label: "East", color: FACING_COLORS.East },
        { label: "West", color: FACING_COLORS.West },
        { label: "North", color: FACING_COLORS.North },
        { label: "South", color: FACING_COLORS.South },
        { label: "East+South", color: FACING_COLORS["East+South"] },
        { label: "East+North", color: FACING_COLORS["East+North"] },
        { label: "West+South", color: FACING_COLORS["West+South"] },
        { label: "West+North", color: FACING_COLORS["West+North"] },
        { label: "East&West", color: FACING_COLORS["East&West"] },
        { label: "North&South", color: FACING_COLORS["North&South"] },
        { label: "All", color: FACING_COLORS.All },
        { label: "N/A", color: FACING_COLORS["N/A"] },
      ];
    }

    return [];
  };

  const refreshMapster = useCallback(() => {
    const imgElement = mapRef.current;
    if (imgElement && $(imgElement).mapster) {
      try {
        $(imgElement).mapster("unbind");
      } catch (e) {
        console.log("Mapster unbind skipped");
      }

      $(imgElement).mapster({
        fillColor: "000000",
        fillOpacity: 0,
        stroke: false,
        singleSelect: true,
        mapKey: "data-key",
        onClick: (data) => {
          if (uiLocked) return false;
          if (!wasDragged && dragDistance < 5) {
            openPlotPanel(data.key);
            $(imgElement).mapster("set", true, data.key);
          }
          return false;
        },
        showToolTip: true,
      });

      if (selectedPlotId) {
        try {
          $(imgElement).mapster("set", true, selectedPlotId);
        } catch (e) {
          console.log("Mapster restore selection skipped");
        }
      }

      if (isIOS()) {
        try {
          $(imgElement).mapster("resize", imgElement.clientWidth, imgElement.clientHeight, 0);
        } catch (e) {
          console.log("Mapster resize skipped");
        }
      }
    }
  }, [wasDragged, dragDistance, openPlotPanel, uiLocked, selectedPlotId]);

  useEffect(() => {
    if (!isImageLoaded) return;

    let isMounted = true;
    const imgElement = mapRef.current;

    const initializeMapster = () => {
      if (imgElement && isMounted) {
        setImageDimensions({
          width: imgElement.naturalWidth,
          height: imgElement.naturalHeight,
        });

        $(imgElement).mapster({
          fillColor: "000000",
          fillOpacity: 0,
          stroke: false,
          singleSelect: true,
          mapKey: "data-key",
          onClick: (data) => {
            if (uiLocked) return false;
            if (!wasDragged && dragDistance < 5) {
              openPlotPanel(data.key);
              $(imgElement).mapster("set", true, data.key);
            }
            return false;
          },
          showToolTip: true,
        });

        if (selectedPlotId) {
          try {
            $(imgElement).mapster("set", true, selectedPlotId);
          } catch (e) {
            console.log("Mapster restore selection skipped");
          }
        }

        if (isIOS()) {
          try {
            $(imgElement).mapster("resize", imgElement.clientWidth, imgElement.clientHeight, 0);
          } catch (e) {
            console.log("Mapster resize init skipped");
          }
        }
      }
    };

    if (imgElement && imgElement.complete) {
      initializeMapster();
    } else if (imgElement) {
      imgElement.onload = () => initializeMapster();
    }

    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (isClickOnUI(e.target)) return;
      if (uiLocked || selectedPlotId) return;

      e.preventDefault();
      const zoomFactor = e.deltaY > 0 ? -zoomStep : zoomStep;

      setZoom((prevZoom) => {
        const newZoom = Math.min(maxZoom, Math.max(minZoom, prevZoom + zoomFactor));
        sessionStorage.setItem(ZOOM_KEY, newZoom.toString());
        setPosition((prevPosition) => adjustPositionToBounds(newZoom, prevPosition));
        if (isIOS()) setTimeout(refreshMapster, 100);
        return newZoom;
      });
    };

    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);

      const newZoom = newWidth <= 1024 ? 3 : 2;
      setZoom(newZoom);
      sessionStorage.setItem(ZOOM_KEY, newZoom.toString());
      setPosition({ x: 0, y: 0 });
      sessionStorage.setItem(POSITION_KEY, JSON.stringify({ x: 0, y: 0 }));

      refreshMapster();
    };

    const debouncedHandleResize = debounce(handleResize, 200);

    container.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("resize", debouncedHandleResize);

    fetchNewsData();
    fetchPlotsData();

    return () => {
      isMounted = false;
      if (imgElement) {
        try {
          $(imgElement).mapster("unbind");
        } catch (e) {
          console.log("Mapster cleanup skipped");
        }
      }
      container.removeEventListener("wheel", handleWheel);
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, [
    isImageLoaded,
    wasDragged,
    dragDistance,
    openPlotPanel,
    refreshMapster,
    minZoom,
    maxZoom,
    selectedPlotId,
    adjustPositionToBounds,
    fetchNewsData,
    fetchPlotsData,
    navigate,
    uiLocked,
    isClickOnUI,
  ]);

  const handleMouseDown = (e) => {
    if (isClickOnUI(e.target)) return;
    if (uiLocked || selectedPlotId) return;

    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    setDragDistance(0);
    setWasDragged(false);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (isClickOnUI(e.target)) return;
      if (uiLocked || !isDragging) return;

      const { minX, maxX, minY, maxY } = calculateBoundaries(zoom);
      let newX = e.clientX - dragStart.x;
      let newY = e.clientY - dragStart.y;

      newX = parseFloat(Math.min(maxX, Math.max(minX, newX)).toFixed(2));
      newY = parseFloat(Math.min(maxY, Math.max(minY, newY)).toFixed(2));

      setPosition(() => {
        const newPosition = { x: newX, y: newY };
        sessionStorage.setItem(POSITION_KEY, JSON.stringify(newPosition));
        return newPosition;
      });

      const distance = Math.hypot(
        e.clientX - (dragStart.x + position.x),
        e.clientY - (dragStart.y + position.y)
      );
      setDragDistance(distance);
      if (distance >= 5) setWasDragged(true);
    },
    [uiLocked, isDragging, dragStart, position, zoom, calculateBoundaries, isClickOnUI]
  );

  const handleMouseUp = (e) => {
    if (isClickOnUI(e.target)) return;
    setIsDragging(false);

    if (dragDistance >= 5) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isIOS()) setTimeout(refreshMapster, 100);
  };

  const handleTouchStart = (e) => {
    if (isClickOnUI(e.target)) return;
    if (uiLocked || selectedPlotId) return;

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
      setLastTouch({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const distance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      setTouchDistance(distance);
      setIsDragging(false);
    }
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (isClickOnUI(e.target)) return;
      if (uiLocked) return;

      const { minX, maxX, minY, maxY } = calculateBoundaries(zoom);

      if (e.touches.length === 1 && isDragging) {
        const touch = e.touches[0];
        let newX = touch.clientX - dragStart.x;
        let newY = touch.clientY - dragStart.y;

        newX = parseFloat(Math.min(maxX, Math.max(minX, newX)).toFixed(2));
        newY = parseFloat(Math.min(maxY, Math.max(minY, newY)).toFixed(2));

        setPosition(() => {
          const newPosition = { x: newX, y: newY };
          sessionStorage.setItem(POSITION_KEY, JSON.stringify(newPosition));
          return newPosition;
        });

        e.preventDefault();
      } else if (e.touches.length === 2) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const newDistance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

        if (touchDistance) {
          const zoomFactor = newDistance / touchDistance;
          setZoom((prevZoom) => {
            const newZoom = Math.min(maxZoom, Math.max(minZoom, prevZoom * zoomFactor));
            sessionStorage.setItem(ZOOM_KEY, newZoom.toString());
            setPosition((prevPosition) => adjustPositionToBounds(newZoom, prevPosition));
            if (isIOS()) setTimeout(refreshMapster, 100);
            return newZoom;
          });
          setTouchDistance(newDistance);
        }
        e.preventDefault();
      }
    },
    [
      uiLocked,
      isDragging,
      dragStart,
      touchDistance,
      zoom,
      minZoom,
      maxZoom,
      refreshMapster,
      adjustPositionToBounds,
      calculateBoundaries,
      isClickOnUI,
    ]
  );

  const handleTouchEnd = (e) => {
    if (isClickOnUI(e.target)) return;
    if (uiLocked) return;

    if (e.touches.length < 2) setTouchDistance(null);

    if (e.touches.length === 0 && isDragging && lastTouch) {
      const touch = e.changedTouches[0];
      const movedDistance = Math.hypot(touch.clientX - lastTouch.x, touch.clientY - lastTouch.y);

      if (movedDistance < 5) {
        const imgElement = mapRef.current;
        if (imgElement) {
          const clickEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            clientX: touch.clientX,
            clientY: touch.clientY,
          });
          imgElement.dispatchEvent(clickEvent);
        }
      }

      setIsDragging(false);
      setLastTouch(null);
      if (isIOS()) setTimeout(refreshMapster, 100);
    }
  };

  const handleContextMenu = (e) => e.preventDefault();

  const zoomIn = () => {
    if (uiLocked || selectedPlotId) return;
    setZoom((prev) => {
      const newZoom = Math.min(maxZoom, prev + zoomStep);
      sessionStorage.setItem(ZOOM_KEY, newZoom.toString());
      setPosition((prevPosition) => adjustPositionToBounds(newZoom, prevPosition));
      if (isIOS()) setTimeout(refreshMapster, 100);
      return newZoom;
    });
  };

  const zoomOut = () => {
    if (uiLocked || selectedPlotId) return;
    setZoom((prev) => {
      const newZoom = Math.max(minZoom, prev - zoomStep);
      sessionStorage.setItem(ZOOM_KEY, newZoom.toString());
      setPosition((prevPosition) => adjustPositionToBounds(newZoom, prevPosition));
      if (isIOS()) setTimeout(refreshMapster, 100);
      return newZoom;
    });
  };

  const getResponsivePosition = (baseX, baseY, offsetX = 0, offsetY = 0) => {
    if (imageDimensions.width === 0 || imageDimensions.height === 0) {
      return { left: `${baseX + offsetX}px`, top: `${baseY + offsetY}px` };
    }
    const imgElement = mapRef.current;
    const scaleX = imgElement ? imgElement.clientWidth / imageDimensions.width : 1;
    const scaleY = imgElement ? imgElement.clientHeight / imageDimensions.height : 1;
    return {
      left: `${(baseX + offsetX) * scaleX}px`,
      top: `${(baseY + offsetY) * scaleY}px`,
    };
  };

  const labelStyle = getLabelStyle();

  const newsTickerText =
    newsItems.length > 0 ? newsItems.join("  •  ") : "No news available";
  const duplicatedNewsText = `${newsTickerText}  •  ${newsTickerText}`;

  const legendItems = getLegendItems();

  if (!isImageLoaded) {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "250px",
          height: "220px",
          background: "rgba(0, 0, 0, 0.7)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1001,
          fontFamily: "Arial, sans-serif",
          fontSize: "20px",
          fontWeight: "bold",
          borderRadius: "30px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="map-container"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={handleContextMenu}
      style={{
        overflow: "hidden",
        position: "relative",
        cursor: uiLocked ? "default" : isDragging ? "grabbing" : "grab",
        WebkitTapHighlightColor: "transparent",
        width: "100%",
        height: "100vh",
      }}
    >
      {error && (
        <div
          style={{
            position: "fixed",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ffe6e6",
            color: "red",
            padding: "10px",
            borderRadius: "8px",
            zIndex: 99999,
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "14px",
          }}
          data-ui="true"
        >
          {error}
        </div>
      )}

      {/* Top right inline toolbar */}
      <div
        style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          zIndex: 1500,
          display: "flex",
          justifyContent: "flex-end",
          width: windowWidth <= 768 ? "calc(100% - 20px)" : "auto",
          pointerEvents: "none",
        }}
        data-ui="true"
      >
        <div
          style={{
            pointerEvents: "auto",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: windowWidth <= 768 ? "4px" : "6px",
            padding: "4px",
            borderRadius: "14px",
            background: "rgba(235, 235, 235, 0.2)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
            width: windowWidth <= 768 ? "280px" : "auto",
            maxWidth: windowWidth <= 768 ? "280px" : "980px",
          }}
        >
          <div
            style={{
              flex: windowWidth <= 768 ? "1 1 100%" : "0 1 250px",
              minWidth: windowWidth <= 768 ? "92%" : "220px",
              maxWidth: windowWidth <= 768 ? "92%" : "280px",
              height: "34px",
              borderRadius: "10px",
              background: "rgba(255, 255, 255, 0.62)",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            <span
              style={{
                display: "inline-block",
                whiteSpace: "nowrap",
                fontSize: windowWidth <= 768 ? "10px" : "11px",
                fontWeight: 600,
                color: "#111827",
                animation:
                  newsItems.length > 0 && newsItems[0] !== "Error fetching news"
                    ? "scroll 30s linear infinite"
                    : "none",
              }}
            >
              {duplicatedNewsText}
            </span>
          </div>

          <div
            style={{
              minWidth: windowWidth <= 768 ? "88px" : "88px",
              height: "34px",
              padding: "0 10px",
              borderRadius: "10px",
              background: "rgba(255, 255, 255, 0.62)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: windowWidth <= 768 ? "10px" : "11px",
              fontWeight: 700,
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            Avl Units: {availableUnits}
          </div>

          <select
            value={colorMode}
            onChange={(e) => {
              setColorMode(e.target.value);
              if (e.target.value !== "status") setStatusFilter("ALL");
              if (e.target.value !== "facing") setFacingFilter("ALL");
            }}
            style={{
              width: windowWidth <= 768 ? "92px" : "104px",
              height: "34px",
              padding: "0 10px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              outline: "none",
              background: "rgba(255,255,255,0.96)",
              fontSize: "11px",
              fontFamily: "'Montserrat', sans-serif",
              cursor: "pointer",
              color: "#111827",
            }}
          >
            <option value="none">Select Filter</option>
            <option value="status">Status</option>
            <option value="facing">Facing</option>
          </select>

          {colorMode === "status" && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: windowWidth <= 768 ? "104px" : "112px",
                height: "34px",
                padding: "0 10px",
                borderRadius: "10px",
                border: "1px solid #d1d5db",
                outline: "none",
                background: "rgba(255,255,255,0.96)",
                fontSize: "11px",
                fontFamily: "'Montserrat', sans-serif",
                cursor: "pointer",
                color: "#111827",
              }}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          )}

          {colorMode === "facing" && (
            <select
              value={facingFilter}
              onChange={(e) => setFacingFilter(e.target.value)}
              style={{
                width: windowWidth <= 768 ? "118px" : "128px",
                height: "34px",
                padding: "0 10px",
                borderRadius: "10px",
                border: "1px solid #d1d5db",
                outline: "none",
                background: "rgba(255,255,255,0.96)",
                fontSize: "11px",
                fontFamily: "'Montserrat', sans-serif",
                cursor: "pointer",
                color: "#111827",
              }}
            >
              {facingOptions.map((facing) => (
                <option key={facing} value={facing}>
                  {facing}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Legend */}
      {colorMode !== "none" && (
        <div
          data-ui="true"
          style={{
            position: "fixed",
            top: "52%",
            left: windowWidth <= 768 ? "8px" : "12px",
            transform: "translateY(-50%)",
            zIndex: 1500,
            width: windowWidth <= 768 ? "82px" : "98px",
            maxHeight: windowWidth <= 768 ? "48vh" : "56vh",
            overflowY: "auto",
            background: "rgba(255,255,255,0.94)",
            borderRadius: "14px",
            padding: windowWidth <= 768 ? "8px" : "10px",
            boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
            fontFamily: "'Montserrat', sans-serif",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              fontSize: windowWidth <= 768 ? "11px" : "12px",
              fontWeight: 700,
              marginBottom: "8px",
              color: "#1f2937",
            }}
          >
            Legend
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "6px",
            }}
          >
            {legendItems.map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: windowWidth <= 768 ? "10px" : "11px",
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                <span
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "3px",
                    background: item.color,
                    border: "1px solid rgba(0,0,0,0.15)",
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zoom / Pan Wrapper */}
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          WebkitTransform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transformOrigin: "center center",
          transition: !isDragging && !touchDistance ? "transform 0.2s ease-out" : "none",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "inline-block",
            lineHeight: 0,
          }}
        >
          <img
            ref={mapRef}
            src={image}
            useMap="#projectMap"
            alt="Project Map"
            style={{
              maxWidth: "100%",
              maxHeight: "100vh",
              width: "auto",
              height: "auto",
              userSelect: "none",
              display: "block",
              imageRendering: "crisp-edges",
              pointerEvents: "auto",
            }}
            onLoad={(e) => {
              const imgEl = e.currentTarget;
              setImageDimensions({
                width: imgEl.naturalWidth,
                height: imgEl.naturalHeight,
              });
            }}
          />

          {imageDimensions.width > 0 && imageDimensions.height > 0 && (
            <svg
              viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
              preserveAspectRatio="xMidYMid meet"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 20,
                overflow: "visible",
              }}
            >
              {plotCoordinates.map((plot) => {
                const fill = getPlotFilterFill(plot.id);
                if (!fill) return null;

                return (
                  <polygon
                    key={`filter-${plot.id}`}
                    points={coordsToSvgPoints(plot.coords)}
                    fill={fill}
                    stroke="rgba(255,255,255,0.18)"
                    strokeWidth="0.25"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}

              {!selectedPlot && hoveredPlot && (
                <polygon
                  points={coordsToSvgPoints(hoveredPlot.coords)}
                  fill="rgba(77, 255, 0, 0.57)"
                  stroke="#ffffff"
                  strokeWidth="0.45"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}

              {selectedPlot && (
                <polygon
                  points={coordsToSvgPoints(selectedPlot.coords)}
                  fill="rgba(77, 255, 0, 0.60)"
                  stroke="#ffffff"
                  strokeWidth="0.55"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </svg>
          )}

          <MapAreas
            onPlotEnter={(plotId) => {
              if (!selectedPlotId) setHoveredPlotId(plotId);
            }}
            onPlotLeave={() => {
              setHoveredPlotId(null);
            }}
          />

          <LocationLabels
            labelStyle={labelStyle}
            getResponsivePosition={getResponsivePosition}
            coords={coords}
            showStatusLabels={showStatusLabels}
          />
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="zoom-controls" data-ui="true">
        <button onClick={zoomIn}>+</button>
        <button onClick={zoomOut}>-</button>
      </div>

      {/* ControlBar */}
      <ControlBar
        showStatusLabels={showStatusLabels}
        onToggleStatusLabels={() => setShowStatusLabels((s) => !s)}
      />

      {/* Compass */}
      <img
        src={compass}
        alt="compass"
        style={{
          position: "fixed",
          bottom: windowWidth <= 768 ? "10px" : "10px",
          left: "10px",
          width: windowWidth <= 768 ? "50px" : "60px",
          height: "auto",
          zIndex: 1500,
          pointerEvents: "auto",
          borderRadius: windowWidth <= 768 ? "20px" : "28px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.6)",
          background: "linear-gradient(180deg, rgba(54, 53, 53, 0.77), rgba(14, 1, 1, 0.57))",
          padding: "10px",
        }}
        data-ui="true"
      />

      {/* Footer credit */}
      <div
        style={{
          position: "fixed",
          bottom: "10px",
          right: "9px",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "9px",
          color: "white",
          borderRadius: "5px",
          textAlign: "end",
          zIndex: 1500,
          pointerEvents: "none",
        }}
      >
        <strong>360° Interactive Virtual tour</strong>
        <br />
        Powered by Luminexa Technologies
      </div>

      {/* RIGHT-side villa panel */}
      {selectedPlotId && (
        <PlotPanel
          selectedPlotId={selectedPlotId}
          selectedPlotData={selectedPlotData}
          panelLoading={panelLoading}
          closePlotPanel={closePlotPanel}
          windowWidth={windowWidth}
        />
      )}

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default Map;